import React, { useState } from "react";
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { updateAppearanceConfig } from "store/slices/kiosksSlice";
import { UpdateAppearanceConfigPayload } from "types/kioskTypes";
import { hasPermission } from "utils/utils";
import { useTranslation } from "react-i18next";
import { OpButton } from "components/customAntd/DLS/OpButton/OpButton";
import { OpModal } from "components/customAntd/DLS/OpModal/OpModal";

interface AppearanceContentProps {
    appearanceForm: any;
}

const AppearanceContent: React.FC<AppearanceContentProps> = ({ appearanceForm }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const selectedKiosk = useSelector((state: RootState) => state.kiosks.selectedKiosk);

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasKioskRead = hasPermission(tokenScopeList, orgId, 'o', 'kiosk:r');
    const hasKioskWrite = hasPermission(tokenScopeList, orgId, 'o', 'kiosk:w');

    const [previewVisible, setPreviewVisible] = useState(false);

    const { t } = useTranslation();

    const handleSubmit = async (args: IOnSubmitArgs<UpdateAppearanceConfigPayload>) => {
        if (!orgId) {
            return;
        }
        const { values } = args;

        const payload: Partial<UpdateAppearanceConfigPayload> = {
            welcomeText: values.welcomeText,
            completedText: values.completedText,
            logoUrl: values.logoUrl || "",
            backgroundUrl: values.backgroundUrl === null ? "" : values.backgroundUrl,
        };

        if (selectedKiosk) {
            dispatch(updateAppearanceConfig({ orgId, kioskId: selectedKiosk.id, appearanceConfig: payload }));
        }
    };

    const backgroundUrl = OpForm.useWatch("backgroundUrl", appearanceForm);
    let logoUrl = OpForm.useWatch("logoUrl", appearanceForm);
    if (logoUrl === "") {
        logoUrl = "/images/Alta_Visitor_Vertical_Blue.png";
    }

    return (
        <>
            <OpForm
                form={appearanceForm}
                onSubmit={handleSubmit}
                hasError={false}
                defaultButtons={false}
                initialValues={{
                    welcomeText: selectedKiosk?.appearance[0]?.welcomeText,
                    completedText: selectedKiosk?.appearance[0]?.completedText,
                    logoUrl: selectedKiosk?.appearance[0]?.logoUrl || "",
                    backgroundUrl: selectedKiosk?.appearance[0]?.backgroundUrl || null,
                }}
                isReadOnly={!hasKioskWrite && hasKioskRead}
            >
                <OpForm.TextAreaInput rows={4} label={t('kiosk.form.welcomeText')} name="welcomeText" />
                <OpForm.TextAreaInput rows={4} label={t('kiosk.form.completedText')} name="completedText" />

                <OpForm.Input label={t('kiosk.form.logoUrl')} name="logoUrl" placeholder="https://example.com/logo.png" />

                <OpForm.Select
                    label={t('kiosk.form.background')}
                    name="backgroundUrl"
                    options={[
                        { label: t('kiosk.form.noBackground'), value: null },
                        { label: t('kiosk.form.backgroundStyle1'), value: "https://invisit.us/images/bg/bg001.jpg" },
                        { label: t('kiosk.form.backgroundStyle2'), value: "https://invisit.us/images/bg/bg002.jpg" },
                        { label: t('kiosk.form.backgroundStyle3'), value: "https://invisit.us/images/bg/bg003.jpg" },
                        { label: t('kiosk.form.backgroundStyle4'), value: "https://invisit.us/images/bg/bg004.jpg" },
                        { label: t('kiosk.form.backgroundStyle5'), value: "https://invisit.us/images/bg/bg005.jpg" },
                        { label: t('kiosk.form.backgroundStyle6'), value: "https://invisit.us/images/bg/bg006.jpg" },
                    ]}
                />

                <OpButton style={{ marginTop: 16 }} onClick={() => setPreviewVisible(true)}>
                    {t('kiosk.form.preview')}
                </OpButton>
            </OpForm>

            <OpModal
                title={t('kiosk.form.preview')}
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width={700}
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: 640,
                        background: 'white',
                        border: '1px solid #ccc',
                        borderRadius: 16,
                        overflow: 'hidden'
                    }}
                >
                    {backgroundUrl && (
                        <img
                            src={backgroundUrl}
                            alt="Background"
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}

                        />
                    )}

                    {logoUrl && (
                        <img
                            src={logoUrl}
                            alt="Logo"
                            style={{
                                position: 'absolute',
                                top: '20%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                maxWidth: '55%',
                                maxHeight: 120,
                                objectFit: 'contain',
                            }}
                        />
                    )}

                    {[0, 1, 2, 3].map((i) => (
                        <img
                            key={i}
                            src="images/transparent_textbox.png"
                            alt="Textbox"
                            style={{
                                position: 'absolute',
                                top: `${250 + i * 75}px`,
                                left: '50%',
                                transform: 'translate(-50%, -20%)',
                                width: '70%',
                                height: '5%'

                            }}
                        />
                    ))}

                </div>

            </OpModal>

        </>
    );
};

export default AppearanceContent;
